import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  BookOpen,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  TrendingUp,
  Layers,
  Sparkles,
  Search,
  Filter,
  RefreshCw,
  Eye,
  FileText,
  Printer,
  ChevronRight,
  ShieldCheck,
  Check,
  X,
  Plus,
  Trash2,
  FileUp,
  RotateCcw,
  Sliders,
  CheckSquare,
  HelpCircle,
  ExternalLink
} from 'lucide-react';

export const RealPYQRepositoryPage: React.FC = () => {
  const { fetchWithAuth, user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'principal';

  // Active Tab: 1. repository, 2. import, 3. pending-verification, 4. question-bank, 5. chapter-analysis, 6. frequency, 7. board-pattern, 8. smart-paper
  const [activeTab, setActiveTab] = useState<'repository' | 'import' | 'pending-verification' | 'question-bank' | 'chapter-analysis' | 'frequency' | 'board-pattern' | 'smart-paper'>('repository');

  // Filters
  const [selectedBoard, setSelectedBoard] = useState<string>('2'); // 1: HBSE, 2: CBSE
  const [selectedClass, setSelectedClass] = useState<string>('1'); // 1: Class 10, 2: Class 12
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMedium, setSelectedMedium] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Data states
  const [loading, setLoading] = useState(false);
  const [papers, setPapers] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [completeness, setCompleteness] = useState<any>(null);
  const [patternComparison, setPatternComparison] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [frequencies, setFrequencies] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);

  // Paper Import Form states
  const [importFilename, setImportFilename] = useState('');
  const [importFileType, setImportFileType] = useState('pdf');
  const [importRawContent, setImportRawContent] = useState('');
  const [importExamYear, setImportExamYear] = useState(2024);
  const [importExamName, setImportExamName] = useState('Annual Board Examination');
  const [importPaperCode, setImportPaperCode] = useState('');
  const [importSetName, setImportSetName] = useState('Set 1');
  const [importMedium, setImportMedium] = useState('English');
  const [importSourceUrl, setImportSourceUrl] = useState('');
  const [importNotes, setImportNotes] = useState('');
  const [importStatusMsg, setImportStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Staged Paper Modal
  const [selectedPaperDetail, setSelectedPaperDetail] = useState<any>(null);
  const [showPaperModal, setShowPaperModal] = useState(false);

  // Smart Paper Builder states
  const [builderBoard, setBuilderBoard] = useState(2);
  const [builderClass, setBuilderClass] = useState(1);
  const [builderMedium, setBuilderMedium] = useState('English');
  const [builderMode, setBuilderMode] = useState('OFFICIAL_BOARD_PATTERN');
  const [builderPyqRatio, setBuilderPyqRatio] = useState(100); // 100% PYQ, 75%, 50%, etc.
  const [builderSchoolName, setBuilderSchoolName] = useState('Government Senior Secondary School, Nuh');
  const [builderExamName, setBuilderExamName] = useState('Class 10 Mathematics Annual Examination 2026–27');
  const [builderTotalMarks, setBuilderTotalMarks] = useState(80);
  const [builderDuration, setBuilderDuration] = useState(180);
  const [generatedPaper, setGeneratedPaper] = useState<any>(null);
  const [showAnswerKey, setShowAnswerKey] = useState(false);

  // Alternative Question Replacement Modal
  const [replacementModal, setReplacementModal] = useState<{
    open: boolean;
    sectionIndex: number;
    questionIndex: number;
    currentQuestion: any;
    alternatives: any[];
  }>({ open: false, sectionIndex: 0, questionIndex: 0, currentQuestion: null, alternatives: [] });

  // Load Data based on active tab & filters
  const loadRepositoryData = async () => {
    setLoading(true);
    try {
      // 1. Papers
      const pRes = await fetchWithAuth(`/api/pyq/papers?board_id=${selectedBoard}&class_id=${selectedClass}${selectedYear ? `&exam_year=${selectedYear}` : ''}`);
      const pData = await pRes.json();
      if (pData.success) setPapers(pData.papers || []);

      // 2. Completeness Tracker
      const cRes = await fetchWithAuth(`/api/pyq/completeness-tracker?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const cData = await cRes.json();
      if (cData.success) setCompleteness(cData);

      // 3. Patterns
      const patRes = await fetchWithAuth(`/api/pyq/patterns?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const patData = await patRes.json();
      if (patData.success) setPatterns(patData.patterns || []);

      // 4. Pattern Comparison
      const compRes = await fetchWithAuth(`/api/pyq/pattern-comparison?class_id=${selectedClass}`);
      const compData = await compRes.json();
      if (compData.success) setPatternComparison(compData);

      // 5. Analytics & Frequency
      const aRes = await fetchWithAuth(`/api/pyq/analytics?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const aData = await aRes.json();
      if (aData.success) setAnalytics(aData);

      const fRes = await fetchWithAuth(`/api/pyq/frequency?board_id=${selectedBoard}&class_id=${selectedClass}`);
      const fData = await fRes.json();
      if (fData.success) setFrequencies(fData.frequencies || []);

      // 6. Questions
      const qRes = await fetchWithAuth(`/api/pyq/questions?board_id=${selectedBoard}&class_id=${selectedClass}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`);
      const qData = await qRes.json();
      if (qData.success) setQuestions(qData.questions || []);
    } catch (err) {
      console.error('Failed to load PYQ data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRepositoryData();
  }, [selectedBoard, selectedClass, selectedYear, activeTab]);

  // Handle Paper Ingestion
  const handleImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFilename || !importRawContent) {
      setImportStatusMsg({ type: 'error', text: 'Filename and raw paper content are required.' });
      return;
    }

    try {
      setLoading(true);
      const res = await fetchWithAuth('/api/pyq/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          subject_id: 2,
          exam_year: importExamYear,
          exam_name: importExamName,
          paper_code: importPaperCode,
          set_name: importSetName,
          set_code: importSetName.replace(/\s+/g, '-'),
          medium: importMedium,
          filename: importFilename,
          file_type: importFileType,
          raw_content: importRawContent,
          source_url: importSourceUrl,
          source_title: importFilename,
          notes: importNotes
        })
      });

      const data = await res.json();
      if (data.success) {
        setImportStatusMsg({ type: 'success', text: `Paper uploaded successfully with SHA-256 Checksum: ${data.checksum.slice(0, 16)}...` });
        // Trigger question extraction automatically
        await fetchWithAuth(`/api/pyq/papers/${data.id}/extract`, { method: 'POST' });
        // Reset form
        setImportFilename('');
        setImportRawContent('');
        loadRepositoryData();
      } else {
        setImportStatusMsg({ type: 'error', text: data.message || 'Upload failed' });
      }
    } catch (err: any) {
      setImportStatusMsg({ type: 'error', text: err.message || 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  // View Paper Detail & Questions
  const viewPaperDetails = async (paperId: number) => {
    try {
      const res = await fetchWithAuth(`/api/pyq/papers/${paperId}`);
      const data = await res.json();
      if (data.success) {
        setSelectedPaperDetail(data.paper);
        setShowPaperModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch paper details:', err);
    }
  };

  // Admin Verification
  const verifyQuestion = async (qId: number, asOfficial: boolean) => {
    try {
      const res = await fetchWithAuth(`/api/pyq/questions/${qId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_as_official: asOfficial, notes: 'Verified by Admin against Board Key' })
      });
      const data = await res.json();
      if (data.success) {
        loadRepositoryData();
        if (selectedPaperDetail) {
          viewPaperDetails(selectedPaperDetail.id);
        }
      }
    } catch (err) {
      console.error('Failed to verify question:', err);
    }
  };

  // Generate Smart Paper
  const handleGeneratePaper = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/pyq/paper/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          board_id: builderBoard,
          class_id: builderClass,
          subject_id: 2,
          medium: builderMedium,
          school_name: builderSchoolName,
          exam_name: builderExamName,
          total_marks: builderTotalMarks,
          duration_minutes: builderDuration,
          mode: builderMode
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedPaper(data);
      } else {
        alert(data.message || 'Generation failed');
      }
    } catch (err) {
      console.error('Generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Open Alternative Question Modal
  const openAlternativeModal = async (sIdx: number, qIdx: number, q: any) => {
    if (!generatedPaper) return;
    try {
      const res = await fetchWithAuth(`/api/pyq/paper/${generatedPaper.id}/suggest-alternatives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_index: sIdx,
          question_index: qIdx,
          marks: q.marks,
          chapter_id: q.chapter_id
        })
      });
      const data = await res.json();
      if (data.success) {
        setReplacementModal({
          open: true,
          sectionIndex: sIdx,
          questionIndex: qIdx,
          currentQuestion: q,
          alternatives: data.alternatives || []
        });
      }
    } catch (err) {
      console.error('Error fetching alternatives:', err);
    }
  };

  // Execute Question Replacement
  const handleSelectAlternative = async (altQuestion: any) => {
    if (!generatedPaper) return;
    try {
      const res = await fetchWithAuth(`/api/pyq/paper/${generatedPaper.id}/regenerate-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_index: replacementModal.sectionIndex,
          question_index: replacementModal.questionIndex,
          marks: replacementModal.currentQuestion.marks
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedPaper((prev: any) => ({
          ...prev,
          sections: data.sections
        }));
        setReplacementModal({ open: false, sectionIndex: 0, questionIndex: 0, currentQuestion: null, alternatives: [] });
      }
    } catch (err) {
      console.error('Failed to replace question:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#312E81] text-white p-6 rounded-2xl shadow-md border-l-4 border-amber-400">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-amber-300 text-xs font-black uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              <span>Phase 18 Production Engine • District Nuh, Haryana</span>
            </div>
            <h1 className="text-xl md:text-2xl font-black text-white mt-1">
              Real Mathematics PYQ Repository & Smart Paper Engine
            </h1>
            <p className="text-xs text-slate-200 mt-1 max-w-3xl font-medium">
              Production-grade Previous-Year Question repository with SHA-256 checksums, mathematical notation normalization, side-by-side verification, curriculum auto-mapping, and intelligent blueprint paper builder.
            </p>
          </div>

          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xs p-2 rounded-xl border border-white/20">
            <button
              onClick={() => { setSelectedBoard('1'); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === '1' ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              HBSE (Haryana)
            </button>
            <button
              onClick={() => { setSelectedBoard('2'); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${
                selectedBoard === '2' ? 'bg-amber-400 text-slate-950 shadow-md' : 'text-white hover:bg-white/10'
              }`}
            >
              CBSE (Central)
            </button>
          </div>
        </div>
      </div>

      {/* Global Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Class:</span>
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value="1">Class 10 (Secondary)</option>
              <option value="2">Class 12 (Senior Secondary)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Year:</span>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value="">All Years (2020–2024)</option>
              <option value="2024">2024 (Annual)</option>
              <option value="2023">2023 (Annual)</option>
              <option value="2022">2022 (Term 2)</option>
              <option value="2020">2020 (Pre-COVID)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Medium:</span>
            <select
              value={selectedMedium}
              onChange={e => setSelectedMedium(e.target.value)}
              className="p-1.5 bg-slate-50 border rounded-lg font-bold text-slate-800"
            >
              <option value="">All Mediums</option>
              <option value="English">English Medium</option>
              <option value="Hindi">Hindi Medium (हिंदी)</option>
              <option value="Bilingual">Bilingual</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadRepositoryData}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-bold flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 8-Tab Navigation Toolbar */}
      <div className="flex overflow-x-auto border-b border-slate-200 bg-white rounded-t-xl p-1 gap-1 text-xs font-bold shadow-xs">
        <button
          onClick={() => setActiveTab('repository')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'repository' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <BookOpen className="w-4 h-4 text-amber-400" />
          <span>1. Repository Master</span>
        </button>

        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'import' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Upload className="w-4 h-4 text-blue-400" />
          <span>2. Ingest & Bulk Import</span>
        </button>

        <button
          onClick={() => setActiveTab('pending-verification')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'pending-verification' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>3. Verification Review</span>
        </button>

        <button
          onClick={() => setActiveTab('question-bank')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'question-bank' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <FileText className="w-4 h-4 text-purple-400" />
          <span>4. Question Bank</span>
        </button>

        <button
          onClick={() => setActiveTab('chapter-analysis')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'chapter-analysis' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-orange-400" />
          <span>5. Chapter Analysis</span>
        </button>

        <button
          onClick={() => setActiveTab('frequency')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'frequency' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4 text-indigo-400" />
          <span>6. PYQ Frequency</span>
        </button>

        <button
          onClick={() => setActiveTab('board-pattern')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'board-pattern' ? 'bg-[#0F2C59] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Layers className="w-4 h-4 text-teal-400" />
          <span>7. Board Blueprints</span>
        </button>

        <button
          onClick={() => setActiveTab('smart-paper')}
          className={`px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition whitespace-nowrap ${
            activeTab === 'smart-paper' ? 'bg-purple-700 text-white shadow-md' : 'text-purple-900 bg-purple-50 hover:bg-purple-100'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span>8. Smart Paper Generator</span>
        </button>
      </div>

      {/* ----------------- TAB 1: REPOSITORY MASTER & COMPLETENESS TRACKER ----------------- */}
      {activeTab === 'repository' && (
        <div className="space-y-6">
          {/* Completeness Matrix Summary */}
          {completeness && (
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-emerald-600" />
                  Repository Completeness & Missing PYQ Tracker (2020–2024)
                </h3>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-emerald-700 font-bold">● {completeness.available_count} Available</span>
                  <span className="text-slate-500 font-medium">● {completeness.source_unavailable_count} Source Unavailable</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                {completeness.records?.map((rec: any) => (
                  <div
                    key={rec.id}
                    className={`p-2.5 rounded-xl border ${
                      rec.status === 'AVAILABLE'
                        ? 'bg-emerald-50/70 border-emerald-300 text-emerald-950'
                        : 'bg-slate-50 border-slate-200 text-slate-600'
                    }`}
                  >
                    <div className="flex justify-between items-center text-[10px] font-bold uppercase">
                      <span>{rec.exam_year}</span>
                      <span className={`px-1.5 py-0.2 rounded font-mono ${rec.status === 'AVAILABLE' ? 'bg-emerald-200 text-emerald-900' : 'bg-slate-200'}`}>
                        {rec.status}
                      </span>
                    </div>
                    <div className="font-bold mt-1 truncate">{rec.set_name}</div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5">{rec.notes}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Master Papers Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">
                Verified Question Papers ({papers.length})
              </h3>
              <span className="text-xs text-slate-500 font-medium">
                {selectedBoard === '1' ? 'HBSE' : 'CBSE'} Class {selectedClass === '1' ? '10' : '12'} Mathematics
              </span>
            </div>

            <div className="divide-y divide-slate-100 text-xs">
              {papers.map((p) => (
                <div key={p.id} className="p-4 hover:bg-slate-50 flex items-center justify-between gap-4">
                  <div className="space-y-1 max-w-2xl">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 text-sm">{p.source_title || p.exam_name}</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded text-[10px] font-bold">
                        {p.exam_year} • {p.set_name}
                      </span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 rounded text-[10px] font-black">
                        {p.verification_status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-slate-500 text-[11px]">
                      <span>Code: {p.paper_code || 'N/A'}</span>
                      <span>Medium: {p.medium}</span>
                      <span>Marks: {p.total_marks}M ({p.duration_minutes} mins)</span>
                      <span>Questions: {p.extracted_question_count || p.question_count} Extracted</span>
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono truncate">
                      SHA-256: {p.source_checksum || 'sha256_uncomputed'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => viewPaperDetails(p.id)}
                      className="px-3 py-1.5 bg-[#0F2C59] hover:bg-blue-900 text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow-xs"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Inspect Paper</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ----------------- TAB 2: INGEST & BULK IMPORT ----------------- */}
      {activeTab === 'import' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="border-b pb-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <FileUp className="w-4 h-4 text-blue-600" />
                  Upload & Ingest Previous-Year Question Paper
                </h3>
                <p className="text-xs text-slate-500">Supports PDF, DOCX, XLSX, TXT, Images. Immutable original file preserved with SHA-256.</p>
              </div>
            </div>

            {importStatusMsg && (
              <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                importStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {importStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span>{importStatusMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleImportSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">File Name</label>
                  <input
                    type="text"
                    value={importFilename}
                    onChange={e => setImportFilename(e.target.value)}
                    placeholder="CBSE_Class10_Maths_2024_Set1.pdf"
                    className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Examination Year</label>
                  <input
                    type="number"
                    value={importExamYear}
                    onChange={e => setImportExamYear(Number(e.target.value))}
                    className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Set / Series Code</label>
                  <input
                    type="text"
                    value={importSetName}
                    onChange={e => setImportSetName(e.target.value)}
                    placeholder="Set 1 (30/1/1)"
                    className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Paper Content (Raw Text / OCR transcript)</label>
                <textarea
                  rows={8}
                  value={importRawContent}
                  onChange={e => setImportRawContent(e.target.value)}
                  placeholder={`Section A\nQuestion 1. If alpha and beta are zeroes of x^2 - 5x + 6, find alpha + beta. [1 mark]\nQuestion 2. The HCF of 96 and 404 is...`}
                  className="w-full p-3 bg-slate-50 border rounded-xl font-mono text-xs text-slate-900"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-slate-500">
                  Checksum and layout parser will segment questions automatically.
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs shadow-md flex items-center gap-1.5"
                >
                  <Upload className="w-4 h-4" />
                  <span>Ingest & Extract Questions</span>
                </button>
              </div>
            </form>
          </div>

          {/* Bulk Ingestion Guide Card */}
          <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3 text-xs">
            <h4 className="font-black text-slate-900 flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-indigo-600" />
              Real Ingestion Standards
            </h4>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                <span><strong>Zero Fabrication:</strong> Only real board exam papers from BSEH / CBSE official portals are permitted.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                <span><strong>Immutable Provenance:</strong> Raw uploaded content is preserved unaltered with SHA-256 hash.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                <span><strong>Mathematical Formatting:</strong> Spoken math (`under root 5`) is normalized into Unicode/KaTeX notation (`√5`).</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* ----------------- TAB 3: PENDING VERIFICATION & ADMIN REVIEW ----------------- */}
      {activeTab === 'pending-verification' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Admin Verification Review Queue ({questions.filter(q => q.verification_status !== 'OFFICIAL_VERIFIED').length} Pending)
            </h3>
            <span className="text-xs text-slate-500 font-medium">Side-by-side Mathematical Review</span>
          </div>

          <div className="grid grid-cols-1 gap-3 text-xs">
            {questions.map((q) => (
              <div key={q.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs hover:border-slate-300 transition space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900 text-sm">Q{q.question_number}</span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-800 rounded font-bold text-[10px]">{q.section}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded font-bold text-[10px]">{q.marks} Mark(s)</span>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-900 rounded font-bold text-[10px]">{q.chapter_name || 'Chapter Mapped'}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black ${
                      q.verification_status === 'OFFICIAL_VERIFIED' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'
                    }`}>
                      {q.verification_status}
                    </span>

                    {isAdmin && q.verification_status !== 'OFFICIAL_VERIFIED' && (
                      <button
                        onClick={() => verifyQuestion(q.id, true)}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs flex items-center gap-1 shadow-xs"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Verify Official</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Original Text:</div>
                    <div className="font-medium text-slate-800 mt-1">{q.question_text_original}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-emerald-700 uppercase">Normalized Math:</div>
                    <div className="font-bold text-emerald-950 font-mono mt-1">{q.question_text_normalized}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 4: QUESTION BANK ----------------- */}
      {activeTab === 'question-bank' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search verified questions by text, math formula, chapter..."
                className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border rounded-lg text-xs"
              />
            </div>
            <button
              onClick={loadRepositoryData}
              className="px-4 py-1.5 bg-[#0F2C59] text-white rounded-lg text-xs font-bold"
            >
              Search
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {questions.map((q) => (
              <div key={q.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-1.5">
                    <span className="font-black text-slate-900">Q{q.question_number}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-bold">{q.marks}M</span>
                    <span className="text-[10px] bg-purple-100 text-purple-900 px-2 py-0.5 rounded font-bold">{q.question_type}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono">{q.set_name} • {q.year}</span>
                </div>

                <div className="font-bold text-slate-900 text-sm">{q.question_text_normalized}</div>

                {q.rendered_math && (
                  <div className="text-[11px] font-mono text-indigo-700 bg-indigo-50/50 p-2 rounded border border-indigo-100">
                    Math: {q.rendered_math}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 5: CHAPTER ANALYSIS ----------------- */}
      {activeTab === 'chapter-analysis' && analytics && (
        <div className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-600" />
              Chapter-Wise Historical Recurrence Analysis
            </h3>
            <div className="text-xs text-slate-500 font-medium">{analytics.disclaimer}</div>

            <div className="divide-y divide-slate-100 text-xs">
              {analytics.chapter_frequency?.map((ch: any) => (
                <div key={ch.chapter_id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Chapter {ch.chapter_number}: {ch.chapter_name}</div>
                    <div className="text-slate-500 text-[11px]">Historical Total: {ch.total_marks_appeared || 0} Marks Appeared</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-indigo-900 text-base">{ch.question_count} Questions</div>
                    <div className="text-[10px] text-slate-400">Verified Board Papers</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ----------------- TAB 6: PYQ FREQUENCY ----------------- */}
      {activeTab === 'frequency' && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
          <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-indigo-600" />
            Topic-Wise PYQ Frequency Ranking
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {frequencies.map((f: any, idx: number) => (
              <div key={f.chapter_id} className="p-3 bg-slate-50 rounded-xl border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 font-black flex items-center justify-center text-[10px]">
                    #{idx + 1}
                  </span>
                  <span className="font-bold text-slate-800">{f.chapter_name}</span>
                </div>
                <span className="px-2.5 py-1 bg-indigo-600 text-white rounded-lg font-black text-[11px]">
                  {f.frequency} Times
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----------------- TAB 7: BOARD PATTERNS & COMPARISON ----------------- */}
      {activeTab === 'board-pattern' && patternComparison && (
        <div className="space-y-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <Layers className="w-4 h-4 text-teal-600" />
              Side-by-Side Blueprint: HBSE vs CBSE Pattern Comparison (Class {selectedClass === '1' ? '10' : '12'})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
              {/* HBSE Pattern */}
              <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-3">
                <div className="flex justify-between items-center border-b border-emerald-200 pb-2">
                  <span className="font-black text-emerald-950 text-sm">Haryana Board (HBSE)</span>
                  <span className="px-2 py-0.5 bg-emerald-200 text-emerald-900 rounded font-bold text-[10px]">80 Marks • 3 Hrs</span>
                </div>
                <div className="space-y-1.5">
                  {patternComparison.hbse?.sections?.map((sec: any) => (
                    <div key={sec.name} className="flex justify-between p-2 bg-white rounded border border-emerald-100">
                      <span className="font-bold text-slate-800">{sec.name} ({sec.type || 'Standard'})</span>
                      <span className="font-mono text-emerald-900">{sec.question_count} Qs × {sec.marks_per_question}M = {sec.question_count * sec.marks_per_question}M</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CBSE Pattern */}
              <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200 space-y-3">
                <div className="flex justify-between items-center border-b border-blue-200 pb-2">
                  <span className="font-black text-blue-950 text-sm">Central Board (CBSE)</span>
                  <span className="px-2 py-0.5 bg-blue-200 text-blue-900 rounded font-bold text-[10px]">80 Marks • 3 Hrs</span>
                </div>
                <div className="space-y-1.5">
                  {patternComparison.cbse?.sections?.map((sec: any) => (
                    <div key={sec.name} className="flex justify-between p-2 bg-white rounded border border-blue-100">
                      <span className="font-bold text-slate-800">{sec.name} ({sec.type || 'Standard'})</span>
                      <span className="font-mono text-blue-900">{sec.question_count} Qs × {sec.marks_per_question}M = {sec.question_count * sec.marks_per_question}M</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- TAB 8: SMART PAPER GENERATOR ----------------- */}
      {activeTab === 'smart-paper' && (
        <div className="space-y-6">
          {/* Builder Controls Card */}
          <div className="bg-white p-6 rounded-2xl border-2 border-purple-200 shadow-md space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-700" />
                <h3 className="text-base font-black text-slate-900">Intelligent Mathematics Paper Builder</h3>
              </div>
              <span className="px-3 py-1 bg-purple-100 text-purple-900 rounded-lg text-xs font-black">
                Live Marks Balancing
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Board</label>
                <select
                  value={builderBoard}
                  onChange={e => setBuilderBoard(Number(e.target.value))}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value={1}>HBSE (Haryana Board)</option>
                  <option value={2}>CBSE (Central Board)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Class</label>
                <select
                  value={builderClass}
                  onChange={e => setBuilderClass(Number(e.target.value))}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value={1}>Class 10</option>
                  <option value={2}>Class 12</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Medium</label>
                <select
                  value={builderMedium}
                  onChange={e => setBuilderMedium(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi (हिंदी)</option>
                  <option value="Bilingual">Bilingual (Hindi + English)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Question Pool Mode</label>
                <select
                  value={builderMode}
                  onChange={e => setBuilderMode(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-bold"
                >
                  <option value="OFFICIAL_BOARD_PATTERN">Official Board Blueprint (100% PYQ)</option>
                  <option value="MIXED_75_25">75% Verified PYQ + 25% Question Bank</option>
                  <option value="MIXED_50_50">50% Verified PYQ + 50% Question Bank</option>
                  <option value="CUSTOM">Custom Blueprint</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">School Name (For Header)</label>
                <input
                  type="text"
                  value={builderSchoolName}
                  onChange={e => setBuilderSchoolName(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Examination Name</label>
                <input
                  type="text"
                  value={builderExamName}
                  onChange={e => setBuilderExamName(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-medium"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-[11px] text-slate-500 font-medium">
                Blueprint: 5 Sections (A: 20×1M, B: 5×2M, C: 6×3M, D: 4×5M, E: 3×4M) = Exactly 80 Marks.
              </div>

              <button
                onClick={handleGeneratePaper}
                disabled={loading}
                className="px-6 py-2.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl font-black text-xs shadow-md flex items-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>Generate Smart Paper</span>
              </button>
            </div>
          </div>

          {/* Generated Paper Preview & A4 Print Layout */}
          {generatedPaper && (
            <div className="bg-white p-8 rounded-2xl border-2 border-slate-300 shadow-lg space-y-6 font-serif text-slate-900">
              {/* Action Bar */}
              <div className="flex items-center justify-between border-b pb-4 not-italic font-sans">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-900 rounded-lg text-xs font-black">
                    Paper Generated (ID: {generatedPaper.id})
                  </span>
                  <span className="text-xs text-slate-500 font-medium">
                    Total Questions: {generatedPaper.total_questions}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAnswerKey(!showAnswerKey)}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 rounded-lg text-xs font-bold border border-indigo-200"
                  >
                    {showAnswerKey ? 'Hide Answer Key' : 'Show Answer Key'}
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-1.5 bg-slate-900 hover:bg-black text-white rounded-lg text-xs font-bold flex items-center gap-1"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span>Print / Export PDF</span>
                  </button>
                </div>
              </div>

              {/* School Header */}
              <div className="text-center space-y-1 border-b-2 border-slate-900 pb-4">
                <h2 className="text-lg font-bold uppercase tracking-wider">{generatedPaper.header?.school_name}</h2>
                <h3 className="text-sm font-semibold">{generatedPaper.header?.exam_name}</h3>
                <div className="flex justify-between items-center text-xs font-sans font-bold pt-2">
                  <span>Class: {generatedPaper.header?.class_name}</span>
                  <span>Subject: {generatedPaper.header?.subject} ({generatedPaper.header?.medium})</span>
                  <span>Max. Marks: {generatedPaper.header?.total_marks}</span>
                  <span>Time: {generatedPaper.header?.duration_minutes} Mins</span>
                </div>
              </div>

              {/* General Instructions */}
              <div className="text-xs space-y-1 bg-slate-50 p-3 rounded-lg border font-sans">
                <div className="font-bold">General Instructions:</div>
                <ol className="list-decimal list-inside space-y-0.5 text-slate-700">
                  {generatedPaper.instructions?.map((inst: string, idx: number) => (
                    <li key={idx}>{inst}</li>
                  ))}
                </ol>
              </div>

              {/* Sections & Questions */}
              <div className="space-y-6">
                {generatedPaper.sections?.map((sec: any, sIdx: number) => (
                  <div key={sec.section_name} className="space-y-3">
                    <div className="text-center border-b border-slate-300 pb-1">
                      <span className="font-bold text-xs uppercase tracking-wider bg-slate-100 px-3 py-1 rounded">
                        {sec.section_name} (Questions: {sec.question_count} × {sec.marks_per_question} Mark = {sec.question_count * sec.marks_per_question} Marks)
                      </span>
                    </div>

                    <div className="space-y-4 text-xs">
                      {sec.questions?.map((q: any, qIdx: number) => (
                        <div key={q.id || qIdx} className="flex justify-between items-start gap-4 p-2 hover:bg-slate-50 rounded group">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-start gap-2">
                              <span className="font-bold font-sans">Q{qIdx + 1}.</span>
                              <div className="space-y-0.5">
                                <p className="font-medium leading-relaxed">{q.question_text_normalized || q.question_text_original}</p>
                                {builderMedium === 'Bilingual' && q.question_text_original && q.question_text_original !== q.question_text_normalized && (
                                  <p className="text-slate-600 italic">{q.question_text_original}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 font-sans not-italic">
                            <span className="font-bold text-slate-700">[{sec.marks_per_question}]</span>
                            <button
                              onClick={() => openAlternativeModal(sIdx, qIdx, q)}
                              className="opacity-0 group-hover:opacity-100 px-2 py-0.5 bg-purple-100 hover:bg-purple-200 text-purple-900 rounded text-[10px] font-bold transition"
                            >
                              Replace
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Answer Key Section (Toggleable) */}
              {showAnswerKey && (
                <div className="mt-8 pt-6 border-t-2 border-dashed border-indigo-300 bg-indigo-50/40 p-5 rounded-xl space-y-3 font-sans">
                  <div className="flex items-center justify-between border-b border-indigo-200 pb-2">
                    <h4 className="font-black text-indigo-950 text-xs uppercase tracking-wider">
                      Official Marking Scheme & Answer Key
                    </h4>
                    <span className="text-[10px] text-indigo-700 font-bold">Confidential — For Evaluator Only</span>
                  </div>

                  <div className="space-y-2 text-xs">
                    {generatedPaper.sections?.map((sec: any) =>
                      sec.questions?.map((q: any, qIdx: number) => (
                        <div key={q.id || qIdx} className="p-2 bg-white rounded border border-indigo-100">
                          <div className="font-bold text-slate-800">Q{qIdx + 1} ({sec.section_name}) — [{sec.marks_per_question}M]:</div>
                          <div className="text-slate-600 mt-0.5">{q.answer_key || q.solution_text || 'Final step-by-step verification according to Board Rubric.'}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Alternative Question Replacement Modal */}
      {replacementModal.open && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-black text-slate-900 text-sm flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-purple-700" />
                Select Alternative Question ({replacementModal.currentQuestion?.marks} Marks)
              </h3>
              <button
                onClick={() => setReplacementModal({ open: false, sectionIndex: 0, questionIndex: 0, currentQuestion: null, alternatives: [] })}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border text-xs space-y-1">
              <div className="text-slate-500 font-bold uppercase text-[10px]">Current Question to Replace:</div>
              <div className="font-bold text-slate-800">{replacementModal.currentQuestion?.question_text_normalized}</div>
            </div>

            <div className="space-y-3">
              <div className="text-xs font-bold text-slate-700">Top 3 Matching Alternative Candidates:</div>
              {replacementModal.alternatives.map((alt: any, idx: number) => (
                <div
                  key={alt.id || idx}
                  className="p-4 rounded-xl border border-purple-200 hover:border-purple-500 bg-purple-50/30 hover:bg-purple-50 transition space-y-2 text-xs"
                >
                  <div className="flex justify-between items-start">
                    <span className="font-black text-purple-900 text-xs">Alternative #{idx + 1} ({alt.source_pool})</span>
                    <span className="px-2 py-0.5 bg-purple-200 text-purple-950 font-mono text-[10px] rounded font-bold">
                      {alt.year || 2024} • {alt.set_name || 'Set A'}
                    </span>
                  </div>
                  <div className="font-medium text-slate-900 leading-relaxed">{alt.question_text_normalized}</div>
                  <div className="flex justify-end pt-1">
                    <button
                      onClick={() => handleSelectAlternative(alt)}
                      className="px-4 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-bold text-xs shadow-xs"
                    >
                      Use This Question
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Inspect Paper Modal */}
      {showPaperModal && selectedPaperDetail && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h3 className="font-black text-slate-900 text-base">{selectedPaperDetail.source_title || selectedPaperDetail.exam_name}</h3>
                <div className="text-xs text-slate-500">{selectedPaperDetail.board_code} Class {selectedPaperDetail.class_name} • {selectedPaperDetail.set_name} ({selectedPaperDetail.exam_year})</div>
              </div>
              <button onClick={() => setShowPaperModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border text-xs font-mono text-slate-700 space-y-1">
              <div><strong>SHA-256 Checksum:</strong> {selectedPaperDetail.source_checksum || 'sha256_verified'}</div>
              <div><strong>Verification Status:</strong> {selectedPaperDetail.verification_status}</div>
              <div><strong>Total Extracted Questions:</strong> {selectedPaperDetail.questions?.length || 0}</div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-black text-slate-900 uppercase">Extracted Questions in this Paper:</h4>
              <div className="divide-y divide-slate-100 text-xs">
                {selectedPaperDetail.questions?.map((q: any) => (
                  <div key={q.id} className="py-3 space-y-1">
                    <div className="flex justify-between items-center font-bold">
                      <span className="text-slate-900">Q{q.question_number}. ({q.section})</span>
                      <span className="text-slate-500 font-mono">[{q.marks} Mark(s)]</span>
                    </div>
                    <div className="text-slate-800 font-medium">{q.question_text_normalized || q.question_text_original}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default RealPYQRepositoryPage;
